import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { Modal, Button } from './common';
import { useGroup } from '../contexts/GroupContext';
import './AddRelationshipModal.css';

const AddRelationshipModal = ({ sourceNode, onCancel, onSuccess }) => {
    const { isGroupContext, activeGroupId } = useGroup();
    const [targetNodeId, setTargetNodeId] = useState('');
    const [relationshipType, setRelationshipType] = useState('');

    const [searchQuery, setSearchQuery] = useState('');
    const [availableNodes, setAvailableNodes] = useState([]);
    const [filteredNodes, setFilteredNodes] = useState([]);
    const [showDropdown, setShowDropdown] = useState(false);
    const [displayLimit, setDisplayLimit] = useState(50);

    // Fetch initial nodes on mount - scoped to group context if active
    useEffect(() => {
        const fetchNodes = async () => {
            try {
                let nodes;
                if (isGroupContext && activeGroupId) {
                    const res = await api.getGroupNodes(activeGroupId, { limit: 500 });
                    nodes = res.nodes || [];
                } else {
                    nodes = await api.getNodes({ limit: 500 });
                }
                setAvailableNodes(nodes.filter(n => n.id !== sourceNode.id));
            } catch (err) {
            }
        };
        fetchNodes();
    }, [sourceNode.id, isGroupContext, activeGroupId]);

    // Filter nodes based on search query
    useEffect(() => {
        setDisplayLimit(50); // Reset limit when search changes
        if (!searchQuery) {
            setFilteredNodes(availableNodes);
        } else {
            const lowerQuery = searchQuery.toLowerCase();
            setFilteredNodes(availableNodes.filter(n =>
                (n.title || n.label || '').toLowerCase().includes(lowerQuery)
            ));
        }
    }, [searchQuery, availableNodes]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!targetNodeId) return;

        try {
            await api.createRelationship(
                sourceNode.id,
                targetNodeId,
                relationshipType || 'relates_to'
            );
            onSuccess();
        } catch (err) {
            alert("Failed to create relationship: " + err.message);
        }
    };

    const footer = (
        <>
            <Button variant="secondary" onClick={onCancel}>Cancel</Button>
            <Button variant="primary" type="submit" disabled={!targetNodeId} onClick={handleSubmit}>Create Relationship</Button>
        </>
    );

    return (
        <Modal isOpen={true} onClose={onCancel} title="Add Relationship" size="md" footer={footer}>
            <form onSubmit={handleSubmit} className="add-rel-form">
                {/* FROM NODE */}
                <div className="add-rel-form__group">
                    <label className="add-rel-form__label">From Node</label>
                    <div className="add-rel-form__source-node">
                        {sourceNode.title}
                    </div>
                </div>

                {/* RELATIONSHIP TYPE */}
                <div className="add-rel-form__group">
                    <label className="add-rel-form__label">Relationship Type (optional)</label>
                    <input
                        type="text"
                        value={relationshipType}
                        onChange={(e) => setRelationshipType(e.target.value)}
                        placeholder="e.g. supports, contradicts, part_of…"
                        className="add-rel-form__input"
                    />
                </div>

                {/* TO NODE (TARGET) */}
                <div className="add-rel-form__group add-rel-form__group--target">
                    <label className="add-rel-form__label">To Node (Target)</label>
                    <div className="add-rel-form__target-wrapper">
                        <input
                            type="text"
                            placeholder="Select or search for target node..."
                            value={searchQuery}
                            onChange={(e) => {
                                setSearchQuery(e.target.value);
                                if (!e.target.value) setTargetNodeId('');
                                setShowDropdown(true);
                            }}
                            onFocus={() => setShowDropdown(true)}
                            className="add-rel-form__input"
                            required={!targetNodeId}
                            onBlur={() => setTimeout(() => setShowDropdown(false), 200)}
                        />
                        {targetNodeId && (
                            <button
                                type="button"
                                className="btn add-rel-form__clear-btn"
                                onClick={() => { setTargetNodeId(''); setSearchQuery(''); setShowDropdown(true); }}
                            >
                                x
                            </button>
                        )}
                    </div>

                    {/* Search Results Dropdown */}
                    {showDropdown && (
                        <div className="add-rel-dropdown">
                            {filteredNodes.length > 0 ? (
                                filteredNodes.slice(0, displayLimit).map(node => {
                                    const displayTitle = node.title && node.title.length > 60
                                        ? node.title.substring(0, 60) + '...'
                                        : node.title;
                                    return (
                                        <div
                                            key={node.id}
                                            className="add-rel-dropdown__item"
                                            onClick={() => {
                                                setTargetNodeId(node.id);
                                                setSearchQuery(node.title);
                                                setShowDropdown(false);
                                            }}
                                            title={node.title}
                                        >
                                            <span className="add-rel-dropdown__title">{displayTitle}</span>
                                        </div>
                                    );
                                })
                            ) : (
                                <div className="add-rel-dropdown__empty">No nodes found</div>
                            )}
                            {filteredNodes.length > displayLimit && (
                                <div className="add-rel-dropdown__load-more">
                                    <Button
                                        variant="primary"
                                        size="sm"
                                        onMouseDown={(e) => {
                                            e.preventDefault(); // Prevent input blur
                                            e.stopPropagation();
                                            setDisplayLimit(prev => prev + 50);
                                        }}
                                    >
                                        Load More ({filteredNodes.length - displayLimit} remaining)
                                    </Button>
                                </div>
                            )}
                            {filteredNodes.length > 0 && (
                                <div className="add-rel-dropdown__count">
                                    Showing {Math.min(displayLimit, filteredNodes.length)} of {filteredNodes.length} nodes
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </form>
        </Modal>
    );
};

export default AddRelationshipModal;
